/**
 * Table Tennis Scoring Engine
 * Points to win and serves per rotation are configurable (tournament-level).
 */

import { getDeuceTotalPoints } from '../constants/scoring';

const DEFAULT_POINTS = 21;
const DEFAULT_SERVES = 2;

/**
 * Get the next server from total points (no separate serve counter).
 * @param {number} player1Score - Player 1's current score
 * @param {number} player2Score - Player 2's current score
 * @param {string} firstServer - Who served first in the game ('player1' or 'player2')
 * @param {object} options - { pointsToWin: 11|21, servesPerRotation: 2|5 }
 * @returns {object} {server, servesInRotation, shouldSwitch}
 */
export function getNextServer(player1Score, player2Score, firstServer, options = {}) {
  const totalPoints = player1Score + player2Score;
  const pointsToWin = options.pointsToWin ?? DEFAULT_POINTS;
  const servesPerRotation = options.servesPerRotation ?? DEFAULT_SERVES;
  const deuceTotal = getDeuceTotalPoints(pointsToWin);
  const other = firstServer === 'player1' ? 'player2' : 'player1';

  if (totalPoints >= deuceTotal) {
    const server = totalPoints % 2 === 0 ? other : firstServer;
    return {
      server,
      servesInRotation: totalPoints % servesPerRotation,
      shouldSwitch: true
    };
  }
  const pairIndex = Math.floor(totalPoints / servesPerRotation) % 2;
  const server = pairIndex === 0 ? firstServer : other;
  const servesInRotation = totalPoints % servesPerRotation;
  const shouldSwitch = totalPoints > 0 && totalPoints % servesPerRotation === 0;
  return {
    server,
    servesInRotation,
    shouldSwitch
  };
}

/**
 * Get the next server for doubles from total points (no separate serve counter).
 * Rotation: T1P1 -> T2P1 -> T1P2 -> T2P2 -> repeat. At deuce, switch every point.
 * @param {number} player1Score - Team 1's current score
 * @param {number} player2Score - Team 2's current score
 * @param {object} serviceState - Current service state (used for team positions only)
 * @param {object} options - { pointsToWin: 11|21, servesPerRotation: 2|5 }
 * @returns {object} {server, teamServer, servesInRotation, shouldSwitch}
 */
export function getNextServerDoubles(player1Score, player2Score, serviceState, options = {}) {
  const totalPoints = player1Score + player2Score;
  const pointsToWin = options.pointsToWin ?? DEFAULT_POINTS;
  const servesPerRotation = options.servesPerRotation ?? DEFAULT_SERVES;
  const deuceTotal = getDeuceTotalPoints(pointsToWin);
  const inDeuce = totalPoints >= deuceTotal;
  const index = inDeuce ? (totalPoints % 4) : (Math.floor(totalPoints / servesPerRotation) % 4);
  const team = index % 2 === 0 ? 'player1' : 'player2';
  const teamServer = index === 0 ? 'team1Player1' : index === 1 ? 'team2Player1' : index === 2 ? 'team1Player2' : 'team2Player2';
  const servesInRotation = totalPoints % servesPerRotation;
  return {
    server: team,
    teamServer,
    servesInRotation,
    shouldSwitch: inDeuce || (totalPoints % servesPerRotation) === servesPerRotation - 1
  };
}

/**
 * Check if a game is won. Points to win and win-by-2 are configurable per match type.
 * @param {number} player1Score - Player 1's score
 * @param {number} player2Score - Player 2's score
 * @param {string} matchType - 'singles' or 'doubles'
 * @param {object} options - { pointsToWin: 11|21 } (use tournament scoring config for match type)
 * @returns {object} {won: boolean, winner: string|null}
 */
export function isGameWon(player1Score, player2Score, matchType = 'singles', options = {}) {
  const winThreshold = options.pointsToWin ?? DEFAULT_POINTS;

  if (player1Score >= 7 && player2Score === 0) {
    return { won: true, winner: 'player1' };
  }
  if (player2Score >= 7 && player1Score === 0) {
    return { won: true, winner: 'player2' };
  }
  if (player1Score >= winThreshold && player2Score === 0) {
    return { won: true, winner: 'player1' };
  }
  if (player2Score >= winThreshold && player1Score === 0) {
    return { won: true, winner: 'player2' };
  }
  if (player1Score >= winThreshold && player1Score - player2Score >= 2) {
    return { won: true, winner: 'player1' };
  }
  if (player2Score >= winThreshold && player2Score - player1Score >= 2) {
    return { won: true, winner: 'player2' };
  }
  return { won: false, winner: null };
}

/**
 * Check if a match is won (best of 3 games)
 */
export function isMatchWon(gameWinners) {
  const player1Wins = Object.values(gameWinners).filter(w => w === 'player1').length;
  const player2Wins = Object.values(gameWinners).filter(w => w === 'player2').length;
  if (player1Wins >= 2) return { won: true, winner: 'player1' };
  if (player2Wins >= 2) return { won: true, winner: 'player2' };
  return { won: false, winner: null };
}

/**
 * Validate score update based on rules
 */
export function validateScoreUpdate(currentScore, newScore, otherScore) {
  if (newScore < 0) return { valid: false, reason: 'Score cannot be negative' };
  if (newScore < currentScore) return { valid: true, reason: '' };
  if (newScore > currentScore + 1) return { valid: false, reason: 'Score can only increase by 1 point at a time' };
  return { valid: true, reason: '' };
}
